import Cookies from 'js-cookie';
import AppConfig from '../AppConfig';
import claimsAxiosInstance from './claimsAxiosInstance';
import employeeExperienceAxiosInstance from './employeeExperienceAxiosInstance';
import telehealthAxiosInstance from './telehealthAxiosInstance';
import healthCheckupAxiosInstance from './healthCheckupAxiosInstance';
import { COOKIE_CONFIG } from '../constants';

export type JWTPayload = {
  exp?: number;
  memberId: string;
  organisationId: string;
  emailId: string;
  source: 'MEMBERS';
};

function base64UrlDecode(str: string): string {
  str = str.replace(/-/g, '+').replace(/_/g, '/');

  while (str.length % 4) {
    str += '=';
  }

  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

function decodeJWT(token: string): JWTPayload | null {
  const parts = token.split('.');

  if (parts.length !== 3) {
    return null; // Not a valid JWT format
  }

  try {
    const payload: JWTPayload = JSON.parse(base64UrlDecode(parts[1]));
    return payload;
  } catch (e) {
    return null;
  }
}

function isTokenExpired(jwtPayload: JWTPayload): boolean {
  const now = Date.now() / 1000; // current time in seconds
  return !!(jwtPayload.exp && jwtPayload.exp < now);
}

function initAxiosIntances(token: string) {
  claimsAxiosInstance.defaults.headers.common['x-plum-token'] = token;
  telehealthAxiosInstance.defaults.headers.common['x-plum-token'] = token;
  employeeExperienceAxiosInstance.defaults.headers.common['x-plum-token'] =
    token;
  healthCheckupAxiosInstance.defaults.headers.common['x-plum-token'] = token;
}

function decodeAuthToken(token: string): JWTPayload | null {
  if (token) {
    const decoded = decodeJWT(token);
    if (decoded && !isTokenExpired(decoded)) {
      initAxiosIntances(token);
      return decoded;
    }
    return null;
  }
  return null;
}

function getUserInfo(): JWTPayload | null {
  const parsedToken = getToken();
  if (!parsedToken) {
    return null;
  }
  return decodeAuthToken(parsedToken);
}

function setToken(token: string) {
  Cookies.set(AppConfig.JWT_TOKEN_COOKIE_NAME, token, COOKIE_CONFIG);
}

function getToken(): string | null {
  const token = Cookies.get(AppConfig.JWT_TOKEN_COOKIE_NAME);
  if (!token) {
    return null;
  }
  return token;
}

export function removeToken() {
  Cookies.remove(AppConfig.JWT_TOKEN_COOKIE_NAME, COOKIE_CONFIG);
}

function verifyTokenAndInit(token: string) {
  const isTokenValid = decodeAuthToken(token);
  if (isTokenValid) {
    if (typeof window?.NativeBridge?.onNativeLogout === 'function') {
      removeToken();
    }
    setToken(token);
    window.dispatchEvent(
      new Event(AppConfig.TOKEN_ADDED_VIA_EXTERNAL_APPLICATION_EVENT as string)
    );
  } else {
    console.log('INVALID TOKEN:', { token });
  }
}

export {
  getUserInfo,
  initAxiosIntances,
  getToken,
  setToken,
  verifyTokenAndInit,
  decodeAuthToken,
  isTokenExpired,
};
